import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EstadoOrden, TrafficBadge } from "@/components/Atoms";
import { useBackdropClose } from "@/lib/useBackdropClose";
import { ArrowLeft, IdentificationCard, Play, MapPin, Spinner, ShieldCheck, Plus, Trash, CloudArrowDown, UsersThree } from "@phosphor-icons/react";

const SEMAFORO_COLORS = { verde: "#10B981", amarillo: "#F59E0B", rojo: "#EF4444" };

export default function OrderDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const [o, setO] = useState(null);
    const [cedula, setCedula] = useState(user?.cedula || "");
    const [starting, setStarting] = useState(false);
    const [error, setError] = useState("");
    const [gps, setGps] = useState({ lat: null, lng: null, status: "idle" });
    const [showCloseEval, setShowCloseEval] = useState(false);
    const [especialidades, setEspecialidades] = useState([]);

    const load = async () => {
        try { const r = await api.get(`/orders/${id}`); setO(r.data); }
        catch (e) { nav("/orders"); }
    };
    useEffect(() => { load(); }, [id]);
    useEffect(() => { (async () => setEspecialidades((await api.get("/especialidades")).data))(); }, []);

    const requestGps = () => {
        if (!navigator.geolocation) return setGps({ lat: null, lng: null, status: "error" });
        setGps(s => ({...s, status: "loading"}));
        navigator.geolocation.getCurrentPosition(
            pos => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, status: "ok" }),
            () => setGps({ lat: null, lng: null, status: "error" }),
            { enableHighAccuracy: true, timeout: 8000 }
        );
    };

    const startInspection = async () => {
        setError("");
        if (!cedula) { setError("Debes ingresar tu cédula"); return; }
        setStarting(true);
        try {
            const payload = { order_id: o.order_id, technician_cedula: cedula.trim(), gps_lat: gps.lat, gps_lng: gps.lng };
            const r = await api.post("/inspections/start", payload);
            nav(`/inspections/${r.data.inspection_id}`);
        } catch (err) {
            setError(err.response?.data?.detail || "Error iniciando inspección");
        }
        setStarting(false);
    };

    if (!o) return <div className="p-8 text-zinc-500">Cargando…</div>;
    const inspectionStarted = !!o.inspection_id;
    const isTecnico = user?.role === "tecnico";
    const isSupervisor = user?.role === "supervisor";
    const isCliente = user?.role === "cliente";
    const isEvalInicial = o.tipo === "evaluacion_inicial";
    const evalCompletada = !!o.evaluacion_completada;

    const downloadHseBundle = async () => {
        try {
            const r = await api.get(`/orders/${o.order_id}/hse-bundle`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = `expediente_hse_${o.order_number}.zip`; a.click();
            URL.revokeObjectURL(url);
        } catch (e) { alert(e.response?.data?.detail || "Error al descargar"); }
    };

    return (
        <div className="max-w-4xl mx-auto p-4 md:p-8" data-testid="order-detail-page">
            <button onClick={() => nav("/orders")} className="text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-900 flex items-center gap-1 mb-4" data-testid="back-orders">
                <ArrowLeft size={14} /> Volver
            </button>

            <div className="border-b border-zinc-200 pb-6 mb-8 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="font-mono text-xs text-zinc-500 mb-1">
                        Orden de servicio {isEvalInicial && <span className="ml-2 px-1.5 py-0.5 rounded" style={{ background: "#0F766E", color: "white" }}>EVALUACIÓN INICIAL</span>}
                    </div>
                    <h1 className="font-display text-4xl font-black tracking-tighter">{o.order_number}</h1>
                    <p className="text-sm text-zinc-600 mt-1">Norma aplicable: <span className="font-semibold">{o.norma_iso}</span></p>
                </div>
                <EstadoOrden estado={o.estado} />
            </div>

            <div className="grid lg:grid-cols-3 gap-0 border border-zinc-200 mb-8">
                <Info label="Cliente" value={o.client?.razon_social} />
                <Info label="NIT" value={o.client?.nit} mono />
                <Info label="Sistema a revisar" value={o.sistema_a_revisar} />
                <Info label="Dirección" value={o.direccion} />
                <Info label="Encargado en sitio" value={o.encargado_nombre} />
                <Info label="Teléfono encargado" value={o.telefono_encargado} mono />
                <Info label="Email para informe" value={o.email_informe} />
                <Info label="Fecha programada" value={o.fecha_programada?.slice(0, 10)} mono />
                <Info label={isEvalInicial ? "Supervisor asignado" : "Técnico asignado"} value={`${o.technician?.nombre || ""} · CC ${o.technician_cedula}`} />
                {o.especialidad_requerida && <Info label="Especialidad requerida" value={o.especialidad_requerida} />}
                {o.recomendaciones_supervisor && <Info label="Recomendaciones del supervisor" value={o.recomendaciones_supervisor} />}
            </div>

            {/* EVALUACIÓN INICIAL — bloque del Supervisor */}
            {isEvalInicial && (
                <div className="border border-teal-200 p-6 bg-teal-50 mb-6" data-testid="eval-inicial-block">
                    <div className="overline mb-2 flex items-center gap-2" style={{ color: "#0F766E" }}><ShieldCheck size={14} weight="fill" /> Visita del Arquitecto Supervisor</div>
                    <h3 className="font-display text-2xl font-bold tracking-tight mb-3">
                        {evalCompletada ? "Evaluación inicial completada" : "Cerrar evaluación inicial"}
                    </h3>

                    {evalCompletada ? (
                        <div data-testid="eval-completed">
                            <p className="text-sm text-zinc-700 mb-3">Cerrada el {o.cerrada_at?.slice(0, 10)}. Sistemas evaluados:</p>
                            <div className="grid sm:grid-cols-2 gap-2">
                                {(o.sistemas_evaluados || []).map((s, i) => (
                                    <div key={i} className="bg-white border border-zinc-200 p-3 flex items-center gap-3" data-testid={`sistema-row-${i}`}>
                                        <div className="w-3 h-3 rounded-full" style={{ background: SEMAFORO_COLORS[s.semaforo] }} />
                                        <div className="flex-1">
                                            <div className="font-semibold text-sm">{s.nombre}</div>
                                            {s.observacion && <div className="text-xs text-zinc-600">{s.observacion}</div>}
                                            {s.especialidad_sugerida && <div className="text-[11px] text-teal-700 mt-0.5">→ {s.especialidad_sugerida}</div>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {o.observaciones_generales && (
                                <div className="mt-3 pt-3 border-t border-teal-200">
                                    <div className="overline text-[10px] mb-1">Observaciones generales</div>
                                    <p className="text-sm text-zinc-700">{o.observaciones_generales}</p>
                                </div>
                            )}
                        </div>
                    ) : isSupervisor ? (
                        <>
                            <p className="text-sm text-zinc-700 mb-4 max-w-2xl">
                                Como Supervisor asignado, registra los sistemas a mantener y su clasificación por semáforo. Esto desbloquea el agendamiento de visitas técnicas por especialidad para este cliente.
                            </p>
                            <button onClick={() => setShowCloseEval(true)} className="btn-accent flex items-center gap-2" data-testid="open-close-eval">
                                <ShieldCheck size={16} weight="fill" /> Registrar sistemas y cerrar evaluación →
                            </button>
                        </>
                    ) : (
                        <p className="text-sm text-zinc-600">Solo el Supervisor asignado puede cerrar esta evaluación.</p>
                    )}
                </div>
            )}

            {/* HSE — ayudantes + descarga expediente (no aplica a eval inicial) */}
            {!isEvalInicial && (
                <HseSection
                    order={o}
                    canManageHelpers={isSupervisor}
                    onDownload={downloadHseBundle}
                    onReload={load}
                />
            )}

            {/* FLUJO TÉCNICO (no aplica a eval inicial) */}
            {!isEvalInicial && (inspectionStarted ? (
                <div className="border border-zinc-200 p-6 bg-zinc-50 mb-6" data-testid="inspection-link">
                    <div className="overline mb-2">Inspección iniciada</div>
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="font-mono text-xs text-zinc-500">{o.inspection_id}</div>
                            <div className="font-display text-2xl font-bold tracking-tight">Continuar inspección</div>
                            {o.inspection?.estado_salud && <div className="mt-2"><TrafficBadge value={o.inspection.estado_salud} /></div>}
                        </div>
                        <button onClick={() => nav(`/inspections/${o.inspection_id}`)} className="btn-accent" data-testid="go-inspection">Ir a la inspección →</button>
                    </div>
                </div>
            ) : isTecnico ? (
                <div className="border border-zinc-200 p-6 bg-white" data-testid="start-block">
                    <div className="overline mb-2 flex items-center gap-2"><IdentificationCard size={14} /> Validación del técnico</div>
                    <h3 className="font-display text-2xl font-bold tracking-tight mb-3">Iniciar visita</h3>
                    <p className="text-sm text-zinc-600 mb-4 max-w-xl">
                        Para iniciar la inspección debes validar tu cédula. Solo puede iniciarla el técnico asignado (CC <span className="font-mono font-semibold">{o.technician_cedula}</span>). Las horas de ingreso y la ubicación GPS quedarán registradas y no podrán ser modificadas.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-4 max-w-xl">
                        <label className="block">
                            <div className="overline mb-1.5">Tu cédula</div>
                            <input className="input-atia font-mono" value={cedula} onChange={e => setCedula(e.target.value)} placeholder="Ej: 10021821" data-testid="cedula-input" />
                        </label>
                        <div>
                            <div className="overline mb-1.5 flex items-center gap-1"><MapPin size={12} /> Coordenadas GPS</div>
                            {gps.status === "ok" ? (
                                <div className="input-atia font-mono text-xs bg-emerald-50 border-emerald-300">{gps.lat.toFixed(5)}, {gps.lng.toFixed(5)}</div>
                            ) : (
                                <button type="button" onClick={requestGps} className="btn-secondary w-full flex items-center justify-center gap-2" data-testid="capture-gps">
                                    {gps.status === "loading" ? <><Spinner size={14} className="animate-spin" /> Capturando…</> : "Capturar GPS"}
                                </button>
                            )}
                        </div>
                    </div>
                    {error && <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-800 text-sm" data-testid="start-error">{error}</div>}
                    <button onClick={startInspection} disabled={starting} className="btn-accent mt-6 flex items-center gap-2" data-testid="start-inspection">
                        <Play size={16} weight="fill" /> {starting ? "Iniciando…" : "Iniciar inspección y registrar entrada"}
                    </button>
                </div>
            ) : (
                <div className="p-6 border border-dashed border-zinc-300 text-center text-zinc-500 text-sm">
                    Solo el técnico asignado puede iniciar esta inspección.
                </div>
            ))}

            {showCloseEval && (
                <CloseEvalModal
                    order={o}
                    especialidades={especialidades}
                    onClose={() => setShowCloseEval(false)}
                    onDone={() => { setShowCloseEval(false); load(); }}
                />
            )}
        </div>
    );
}

function Info({ label, value, mono }) {
    return (
        <div className="p-5 border-r border-b border-zinc-200 last:border-r-0">
            <div className="overline mb-2">{label}</div>
            <div className={`text-sm font-semibold ${mono ? "font-mono" : ""}`}>{value || "—"}</div>        </div>
    );
}

function CloseEvalModal({ order, especialidades, onClose, onDone }) {
    const backdrop = useBackdropClose(onClose);
    const [sistemas, setSistemas] = useState([
        { nombre: "", semaforo: "verde", observacion: "", especialidad_sugerida: "" },
    ]);
    const [obs, setObs] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const addRow = () => setSistemas(s => [...s, { nombre: "", semaforo: "verde", observacion: "", especialidad_sugerida: "" }]);
    const delRow = (i) => setSistemas(s => s.filter((_, idx) => idx !== i));
    const upd = (i, k, v) => setSistemas(s => s.map((row, idx) => idx === i ? {...row, [k]: v} : row));

    const submit = async () => {
        setError(""); setSubmitting(true);
        try {
            const valid = sistemas.filter(s => s.nombre.trim());
            if (!valid.length) { setError("Registra al menos un sistema."); setSubmitting(false); return; }
            await api.post(`/orders/${order.order_id}/cerrar-evaluacion-inicial`, {
                sistemas: valid.map(s => ({ ...s, especialidad_sugerida: s.especialidad_sugerida || null })),
                observaciones_generales: obs,
            });
            onDone();
        } catch (e) {
            setError(e.response?.data?.detail || "Error al cerrar evaluación");
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" {...backdrop.backdropProps} data-testid="close-eval-modal">
            <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto" {...backdrop.contentProps}>
                <div className="p-6 border-b border-zinc-200">
                    <div className="overline mb-1" style={{ color: "#0F766E" }}>Cierre de evaluación inicial</div>
                    <h2 className="font-display text-3xl font-black tracking-tighter">Sistemas a mantener · semáforo</h2>
                    <p className="text-sm text-zinc-600 mt-1">Clasifica cada sistema según urgencia. Al guardar, se habilita el agendamiento de técnicos por especialidad.</p>
                </div>
                <div className="p-6 space-y-3">
                    {sistemas.map((s, i) => (
                        <div key={i} className="border border-zinc-200 p-3 grid grid-cols-12 gap-2 items-center" data-testid={`eval-row-${i}`}>
                            <input className="input-atia col-span-4" placeholder="Sistema (ej. HVAC central)" value={s.nombre} onChange={e => upd(i, "nombre", e.target.value)} data-testid={`eval-nombre-${i}`} />
                            <select className="input-atia col-span-2" value={s.semaforo} onChange={e => upd(i, "semaforo", e.target.value)} style={{ background: SEMAFORO_COLORS[s.semaforo] + "20", borderColor: SEMAFORO_COLORS[s.semaforo] }} data-testid={`eval-sem-${i}`}>
                                <option value="verde">🟢 Verde</option>
                                <option value="amarillo">🟡 Amarillo</option>
                                <option value="rojo">🔴 Rojo</option>
                            </select>
                            <select className="input-atia col-span-3" value={s.especialidad_sugerida} onChange={e => upd(i, "especialidad_sugerida", e.target.value)} data-testid={`eval-esp-${i}`}>
                                <option value="">Especialidad…</option>
                                {especialidades.map(e => <option key={e} value={e}>{e}</option>)}
                            </select>
                            <input className="input-atia col-span-3" placeholder="Observación" value={s.observacion} onChange={e => upd(i, "observacion", e.target.value)} data-testid={`eval-obs-${i}`} />
                            <button type="button" onClick={() => delRow(i)} className="text-red-500 hover:text-red-700 col-span-12 sm:col-span-12 text-xs flex items-center gap-1" data-testid={`eval-del-${i}`}>
                                <Trash size={12} /> Eliminar
                            </button>
                        </div>
                    ))}
                    <button type="button" onClick={addRow} className="btn-secondary flex items-center gap-2 text-sm" data-testid="eval-add-row">
                        <Plus size={14} /> Agregar sistema
                    </button>
                    <label className="block mt-4">
                        <div className="overline mb-1.5">Observaciones generales (opcional)</div>
                        <textarea rows={3} className="input-atia" value={obs} onChange={e => setObs(e.target.value)} data-testid="eval-obs-gen" placeholder="Resumen ejecutivo de la visita inicial…" />
                    </label>
                    {error && <div className="p-3 border border-red-300 bg-red-50 text-red-900 text-sm" data-testid="eval-error">{error}</div>}
                </div>
                <div className="p-6 border-t border-zinc-200 flex justify-end gap-3">
                    <button onClick={onClose} className="btn-secondary" data-testid="eval-cancel">Cancelar</button>
                    <button onClick={submit} disabled={submitting} className="btn-accent" data-testid="eval-confirm">
                        {submitting ? "Guardando…" : "Cerrar evaluación y habilitar técnicos →"}
                    </button>
                </div>
            </div>
        </div>
    );
}


function HseSection({ order, canManageHelpers, onDownload, onReload }) {
    const helpers = order.helpers || [];
    const [showAdd, setShowAdd] = useState(false);
    const [allTechs, setAllTechs] = useState([]);
    const [selectedCc, setSelectedCc] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        if (canManageHelpers) {
            api.get("/technicians?activo=true").then(r => setAllTechs(r.data));
        }
    }, [canManageHelpers]);

    const addHelper = async () => {
        if (!selectedCc) return;
        setError("");
        const next = [...helpers.map(h => h.cedula), selectedCc];
        try {
            await api.put(`/orders/${order.order_id}/helpers`, { helpers_cedulas: next });
            setSelectedCc(""); setShowAdd(false); onReload();
        } catch (e) { setError(e.response?.data?.detail || "Error"); }
    };

    const removeHelper = async (cc) => {
        if (!window.confirm("¿Quitar ayudante de esta visita?")) return;
        const next = helpers.filter(h => h.cedula !== cc).map(h => h.cedula);
        try {
            await api.put(`/orders/${order.order_id}/helpers`, { helpers_cedulas: next });
            onReload();
        } catch (e) { alert(e.response?.data?.detail || "Error"); }
    };

    const availTechs = allTechs.filter(t =>
        t.cedula !== order.technician_cedula && !helpers.some(h => h.cedula === t.cedula)
    );

    return (
        <div className="border border-zinc-200 p-6 mb-6 bg-white" data-testid="hse-section">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                <div>
                    <div className="overline mb-1 flex items-center gap-1.5" style={{ color: "#0F766E" }}>
                        <UsersThree size={14} weight="fill" /> Personal en visita · ISO 45001
                    </div>
                    <h3 className="font-display text-2xl font-bold tracking-tight">Expediente HSE</h3>
                    <p className="text-xs text-zinc-600 mt-1">Documentos de SST del técnico principal y ayudantes para legalizar el acceso a sitio.</p>
                </div>
                <button onClick={onDownload} className="btn-accent flex items-center gap-2" data-testid="download-hse-zip">
                    <CloudArrowDown size={16} weight="bold" /> Descargar expediente (.zip)
                </button>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mb-4">
                <div className="border border-zinc-200 p-3 text-sm" data-testid="hse-principal">
                    <div className="overline text-[10px] mb-1">Técnico principal</div>
                    <div className="font-semibold">{order.technician?.nombre || order.technician_cedula}</div>
                    <div className="font-mono text-xs text-zinc-500">CC {order.technician_cedula} · {order.technician?.especialidad || "—"}</div>
                </div>
                {helpers.map(h => (
                    <div key={h.cedula} className="border border-zinc-200 p-3 text-sm" data-testid={`helper-${h.cedula}`}>
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="overline text-[10px] mb-1" style={{ color: "#0F766E" }}>Ayudante</div>
                                <div className="font-semibold">{h.nombre}</div>
                                <div className="font-mono text-xs text-zinc-500">CC {h.cedula} · {h.especialidad || "—"}</div>
                            </div>
                            {canManageHelpers && (
                                <button onClick={() => removeHelper(h.cedula)} className="text-red-500 hover:text-red-700 text-xs flex items-center gap-1" data-testid={`remove-helper-${h.cedula}`}>
                                    <Trash size={12} /> Quitar
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {canManageHelpers && (
                showAdd ? (
                    <div className="border border-teal-200 bg-teal-50 p-3 flex flex-wrap items-end gap-3" data-testid="add-helper-form">
                        <label className="flex-1 min-w-[240px]">
                            <div className="overline mb-1.5">Añadir ayudante</div>
                            <select className="input-atia" value={selectedCc} onChange={e => setSelectedCc(e.target.value)} data-testid="helper-select">
                                <option value="">— Selecciona técnico —</option>
                                {availTechs.map(t => <option key={t.cedula} value={t.cedula}>{t.nombre} · CC {t.cedula} · {t.especialidad || "—"}</option>)}
                            </select>
                            <div className="text-[11px] text-zinc-500 mt-1">El ayudante también debe tener expediente HSE vigente.</div>
                        </label>
                        <button onClick={addHelper} className="btn-primary" data-testid="helper-add-btn">Añadir</button>
                        <button onClick={() => { setShowAdd(false); setError(""); }} className="btn-secondary">Cancelar</button>
                        {error && <div className="w-full p-2 border border-red-300 bg-red-50 text-red-900 text-xs" data-testid="helper-error">{error}</div>}
                    </div>
                ) : (
                    <button onClick={() => setShowAdd(true)} className="btn-secondary flex items-center gap-2 text-sm" data-testid="add-helper-btn">
                        <Plus size={14} /> Añadir ayudante
                    </button>
                )
            )}
        </div>
    );
}
